import { inject, Injectable } from '@angular/core';
import { VARIABLES } from '@environments/environment.variables';
import { LogsData } from '@shared/services/logs';
import { PlatformContextData } from '@shared/services/platform-context';
import Dexie, { Table } from 'dexie';
import { catchError, forkJoin, from, map, Observable, of, switchMap, tap, timeout } from 'rxjs';
import { DataTable, StoreName } from './enum';
import type { DataCache } from './models';
import { createDataCache, isDataCache } from './models';

const DATABASE_NAME = 'CacheDB';

/**
 * Reactive cache management service using IndexedDB via Dexie.js.
 *
 * @remarks
 * - Automatically invalidates cache on deployment (build timestamp change)
 * - Expired entries are removed on retrieval
 * - SSR-safe: all operations return no-op Observables on server
 */
@Injectable({ providedIn: 'root' })
export class CacheDB {
  private readonly BUILD_TIMESTAMP = VARIABLES.buildTimestamp;
  private readonly db!: Dexie;
  private readonly logs = inject(LogsData);
  private readonly isServer = inject(PlatformContextData).isServer;

  constructor() {
    if (this.isServer()) {
      return;
    }

    this.db = new Dexie(DATABASE_NAME);
    const dbSchema = {
      [DataTable.CACHE]: 'key',
      [DataTable.STORAGE]: 'key',
      [DataTable.ASSETS]: 'key',
    } as const satisfies Record<StoreName, string>;
    this.db.version(1).stores(dbSchema);
  }

  /**
   * Deletes all data from all stores.
   *
   * @remarks
   * Destructive operation - removes all cached data across all stores.
   */
  clearAllStores(): Observable<void> {
    if (this.isServer()) {
      return of(void 0);
    }
    const tables = [DataTable.CACHE, DataTable.STORAGE, DataTable.ASSETS] as const;
    return forkJoin(tables.map((t) => from(this.db.table(t).clear()))).pipe(
      map(() => void 0),
      tap(() => this.logs.info('CacheDB', 'All stores cleared')),
      catchError((err: unknown) => {
        this.logs.error('CacheDB', 'Error clearing stores', { error: err });
        throw err;
      })
    );
  }

  /**
   * Inserts or updates a cache entry with optional expiration.
   *
   * @param key - Unique cache identifier
   * @param value - Data to cache
   * @param expirationTime - TTL in seconds (0 = permanent)
   * @param storeName - Target store (default: 'STORAGE')
   */
  upsert(key: string, value: unknown, expirationTime = 0, storeName: StoreName = 'STORAGE'): Observable<string> {
    if (this.isServer()) {
      return of(key);
    }
    return from(this.getTable(storeName).put(createDataCache(key, value, expirationTime))).pipe(
      map(() => key),
      tap(() => this.logs.trace('CacheDB', 'Key added successfully', { key, storeName })),
      catchError((err: unknown) => {
        this.logs.error('CacheDB', `Error adding key "${key}"`, { key, storeName, error: err });
        throw err;
      })
    );
  }

  /**
   * Retrieves a cached value with automatic expiration and build validation.
   *
   * @param key - Cache entry identifier
   * @param storeName - Source store (default: 'STORAGE')
   *
   * @remarks
   * Returns `undefined` when entry doesn't exist, is expired, or build version mismatches.
   */
  get<T>(key: string, storeName: StoreName = 'STORAGE'): Observable<T | undefined> {
    if (this.isServer()) {
      return of(undefined);
    }
    const source$ = from(this.getTable(storeName).get(key));
    const safeSource$ = storeName === 'CACHE' ? source$.pipe(timeout(2000)) : source$;

    return safeSource$.pipe(
      switchMap((cache: unknown) => {
        if (!isDataCache<T>(cache)) {
          return of(undefined);
        }

        // Build version mismatch - only applies to CACHE store
        if (storeName === 'CACHE' && cache.buildTimestamp !== this.BUILD_TIMESTAMP) {
          return from(this.db.table(DataTable.CACHE).clear()).pipe(
            tap(() => this.logs.info('CacheDB', 'Cache store cleared (build mismatch)')),
            map(() => undefined),
            catchError(() => of(undefined))
          );
        }

        if (this.isValidCache(cache)) {
          this.logs.trace('CacheDB', 'Get Data', { key, data: cache.data, storeName });
          return of(cache.data);
        }

        // Cache expired - delete and return undefined
        return this.delete(key, storeName).pipe(map(() => undefined));
      }),
      catchError((err: unknown) => {
        this.logs.warn('CacheDB', 'Cache read failed, falling back to network', { key, storeName, error: err });
        return of(undefined);
      })
    );
  }

  /**
   * Removes a cache entry from the specified store.
   *
   * @param key - Entry identifier to delete
   * @param storeName - Target store (default: 'STORAGE')
   */
  delete(key: string, storeName: StoreName = 'STORAGE'): Observable<void> {
    if (this.isServer()) {
      return of(void 0);
    }
    return from(this.getTable(storeName).delete(key)).pipe(
      tap(() => this.logs.trace('CacheDB', 'Key deleted successfully', { key, storeName })),
      catchError((err: unknown) => {
        this.logs.error('CacheDB', `Error deleting key "${key}"`, { key, storeName, error: err });
        throw err;
      })
    );
  }

  private isValidCache<T>(cache: DataCache<T>): boolean {
    return cache.expiredAt === 0 || cache.expiredAt >= Date.now();
  }

  private getTable(storeName: StoreName): Table<DataCache<unknown>, string> {
    return this.db.table(storeName);
  }
}
